import React from 'react'
import { Box } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Section } from '../../common/Section'
import { formatPrice } from '../../../util/formatters'
import { LineChart } from '../../common/charts/LineChart'
import {
  VersionCostCalculator,
  getContainerId,
} from '../../../calculators/costs/VersionCostCalculator'

type Props = {
  versions: VersionCostCalculator[]
  selectedContainerName: string | null
  containerIdsToNames: Record<string, string>
  printPadding?: boolean
}

export function CumulativeCostChart({
  versions,
  selectedContainerName,
  containerIdsToNames,
  printPadding,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const mapYearlyValuesToData = ([year, value]: [number, number]) => ({
    x: year,
    y: value,
  })

  const getCumulativeValues = (version: VersionCostCalculator) => {
    if (selectedContainerName) {
      const containerId = getContainerId(
        version,
        containerIdsToNames,
        selectedContainerName,
      )
      return containerId
        ? version.getAllCumulativeYearlyCostValues(containerId)
        : []
    }
    return version.getAllCumulativeYearlyCostValues()
  }

  const mapVersionToData = (version: VersionCostCalculator) => ({
    id: version.getName(),
    data: getCumulativeValues(version).map(mapYearlyValuesToData),
  })

  const data = versions.map(mapVersionToData)

  const containerHeight = printPadding ? 500 : 550

  return (
    <Section
      title={t('costs.results.cumulativeChartTitle')}
      printPadding={printPadding}
    >
      <Box width="100%" height={containerHeight} borderRadius={6}>
        {data.length > 0 && (
          <LineChart
            data={data}
            xFormatter={String}
            yFormatter={formatPrice}
            xAxisFormatter={String}
          />
        )}
      </Box>
    </Section>
  )
}
